// ---------------------------------------------------------------
// Trips — the execution plan. A Trip is a separate business object from a
// Load (B number). One trip carries one or many loads. When a driver hands the
// load off (relay, breakdown, driver swap, cross-dock), the TRIP NUMBER DOES
// NOT CHANGE — the same trip continues with a new driver/equipment. Each
// driver's stint is a SEGMENT (leg) that records who/what/where/when + miles,
// so every driver's settlement is exactly the portion they actually ran.
//
//   Load    = "what is being hauled"  (freight / customer commitment)
//   Trip    = "how it is being hauled" (the movement — keeps ONE number)
//   Segment = one driver's leg of responsibility (the assignment-history row)
// ---------------------------------------------------------------

import { openDb, getLoads, putLoad, nextTripNumber } from './loadStore';

const STORE = 'trips';

function tx(mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(r && r.result !== undefined ? r.result : r);
    t.onerror = () => reject(t.error);
  }));
}

export const TRIP_STATUSES = [
  'Planning', 'Assigned', 'Dispatched', 'Driver Accepted', 'En Route',
  'At Pickup', 'Loaded', 'In Transit', 'At Delivery', 'Delivered',
  'Completed', 'Settled', 'Archived',
];
export const TRIP_STATUS_COLORS = {
  Planning: '#94a3b8', Assigned: '#6366f1', Dispatched: '#0066ff', 'Driver Accepted': '#0891b2',
  'En Route': '#f59e0b', 'At Pickup': '#8b5cf6', Loaded: '#0891b2', 'In Transit': '#f59e0b',
  'At Delivery': '#8b5cf6', Delivered: '#22c55e', Completed: '#15803d', Settled: '#0f172a', Archived: '#64748b',
};

export async function getTrips() {
  const all = await tx('readonly', (os) => {
    const out = [];
    return new Promise((res) => {
      os.openCursor().onsuccess = (e) => { const c = e.target.result; if (c) { out.push(c.value); c.continue(); } else res(out); };
    });
  });
  return all.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}
export const getTrip = (id) => tx('readonly', (os) => os.get(id));
export const putTrip = (trip) => tx('readwrite', (os) => os.put(trip));
export const deleteTrip = (id) => tx('readwrite', (os) => os.delete(id));

const now = () => new Date().toISOString();
const laneOf = (load) => {
  const pus = (load.stops || []).filter((s) => s.type === 'pickup');
  const dels = (load.stops || []).filter((s) => s.type === 'delivery');
  const a = pus[0] || load.shipper || {}; const b = dels[dels.length - 1] || load.consignee || {};
  return { from: [a.city, a.state].filter(Boolean).join(', '), to: [b.city, b.state].filter(Boolean).join(', ') };
};

// a segment = one driver's leg of the trip (the assignment-history row)
function makeSegment(seq, { driverIds, truckId, trailerId, from, to, reason, loadIds }) {
  return {
    id: `SEG${Date.now()}${Math.floor(Math.random() * 1000)}`,
    seq, driverIds: driverIds || [], truckId: truckId || '', trailerId: trailerId || '',
    from: from || '', to: to || '', startTime: now(), endTime: null, miles: '',
    reason: reason || 'Initial Dispatch', status: 'Active', loadIds: loadIds || [],
  };
}

// mirror the current driver/truck/trailer onto a load (kept for the messaging engine)
async function mirrorToLoad(load, trip, extra = {}) {
  await putLoad({
    ...load, tripId: trip.id,
    vehicleId: trip.truckId, truckId: trip.truckId,
    driverId: (trip.driverIds || [])[0] || load.driverId,
    trailerId: trip.trailerId,
    ...extra,
  });
}

// Create the trip when a load is first assigned a driver + truck + trailer.
export async function createTripForLoad(load, assignment) {
  const tripNumber = assignment.tripNumber || (await nextTripNumber());
  const lane = laneOf(load);
  const trip = {
    id: `T${Date.now()}`,
    tripNumber,
    status: 'Assigned',
    origin: lane.from, destination: lane.to,
    truckId: assignment.truckId || '',
    trailerId: assignment.trailerId || '',
    driverIds: assignment.driverIds || [],
    loadIds: [load.id],
    segments: [makeSegment(1, { driverIds: assignment.driverIds, truckId: assignment.truckId, trailerId: assignment.trailerId, from: lane.from, to: lane.to, reason: 'Initial Dispatch', loadIds: [load.id] })],
    transfers: [],
    createdAt: now(),
    statusHistory: [{ status: 'Assigned', at: now(), note: 'trip created on assignment' }],
  };
  await putTrip(trip);
  await mirrorToLoad(load, trip, { tripHistory: [...(load.tripHistory || []), { tripId: trip.id, tripNumber, from: lane.from, to: lane.to, at: now(), reason: 'Initial Dispatch' }] });
  return { trip, load: { ...load, tripId: trip.id } };
}

// Add another load onto an existing trip (one driver, several B numbers).
// No new segment — the load rides the current leg.
export async function addLoadToTrip(tripId, load) {
  const trip = await getTrip(tripId);
  if (!trip) return null;
  if (!trip.loadIds.includes(load.id)) trip.loadIds.push(load.id);
  const open = [...(trip.segments || [])].reverse().find((s) => !s.endTime);
  if (open && !open.loadIds.includes(load.id)) open.loadIds.push(load.id);
  await putTrip(trip);
  await mirrorToLoad(load, trip, { tripHistory: [...(load.tripHistory || []), { tripId, tripNumber: trip.tripNumber, from: laneOf(load).from, to: laneOf(load).to, at: now(), reason: 'Added to trip' }] });
  return { trip, load };
}

export async function removeLoadFromTrip(tripId, loadId) {
  const trip = await getTrip(tripId);
  if (!trip) return null;
  trip.loadIds = (trip.loadIds || []).filter((id) => id !== loadId);
  trip.segments = (trip.segments || []).map((s) => ({ ...s, loadIds: (s.loadIds || []).filter((id) => id !== loadId) }));
  await putTrip(trip);
  return trip;
}

// Hand the trip off to a new driver/equipment WITHOUT changing the trip number.
// Closes the current driver's leg (at the handoff location) and opens a new leg
// on the SAME trip so each driver keeps exactly their own miles/work.
export async function handoffTrip(tripId, newAssignment, opts = {}) {
  const trip = await getTrip(tripId);
  if (!trip) return null;
  const reason = opts.reason || 'transfer';
  const location = opts.location || '';
  const segs = [...(trip.segments || [])];
  const open = [...segs].reverse().find((s) => !s.endTime);
  const carried = open ? open.loadIds : (trip.loadIds || []);

  // close the outgoing driver's leg
  if (open) {
    open.endTime = now();
    if (location) open.to = location;
    open.status = 'Completed by transfer';
    if (opts.closingMiles) open.miles = opts.closingMiles;
  }

  // open the incoming driver's leg on the same trip
  const nextSeg = makeSegment((segs.length || 0) + 1, {
    driverIds: newAssignment.driverIds, truckId: newAssignment.truckId, trailerId: newAssignment.trailerId,
    from: location || (open ? open.to : trip.origin), to: trip.destination, reason, loadIds: carried,
  });
  trip.segments = [...segs, nextSeg];
  trip.transfers = [...(trip.transfers || []), {
    at: now(), location, reason,
    from: open ? { driverIds: open.driverIds, truckId: open.truckId, trailerId: open.trailerId } : null,
    to: { driverIds: nextSeg.driverIds, truckId: nextSeg.truckId, trailerId: nextSeg.trailerId },
  }];
  // the trip's current driver/equipment become the new leg's
  trip.truckId = newAssignment.truckId || trip.truckId;
  trip.trailerId = newAssignment.trailerId || trip.trailerId;
  trip.driverIds = newAssignment.driverIds || trip.driverIds;
  trip.statusHistory = [...(trip.statusHistory || []), { status: trip.status, at: now(), note: `${reason} at ${location || 'handoff'} — new driver continues (same trip)` }];
  await putTrip(trip);

  // repoint the loads' current driver/equipment (they stay on the same trip)
  const all = await getLoads();
  for (const id of carried) {
    const l = all.find((x) => x.id === id);
    if (l) await mirrorToLoad(l, trip, { statusHistory: [...(l.statusHistory || []), { status: l.status, at: now(), note: `Transferred (${reason})${location ? ' at ' + location : ''} — new driver` }] });
  }
  return trip;
}

export function tripWithStatus(trip, status, note) {
  if (trip.status === status) return trip;
  return { ...trip, status, statusHistory: [...(trip.statusHistory || []), { status, at: now(), note: note || '' }] };
}

export async function loadsForTrip(trip) {
  const all = await getLoads();
  return (trip.loadIds || []).map((id) => all.find((l) => l.id === id)).filter(Boolean);
}

// Every leg a driver ran, across all trips — the basis for their settlement/report.
export async function segmentsForDriver(driverId) {
  const trips = await getTrips();
  const out = [];
  trips.forEach((t) => (t.segments || []).forEach((s) => {
    if ((s.driverIds || []).map(String).includes(String(driverId))) {
      out.push({ tripNumber: t.tripNumber, tripId: t.id, ...s });
    }
  }));
  return out.sort((a, b) => (b.startTime || '').localeCompare(a.startTime || ''));
}
